import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import './styles/index.css';

import {
  NAV_ITEMS,
} from './constants';
import { Navigation } from './features/Navigation';
import { HeroSection, AboutSection } from './features/Hero';
import { TechStackSection } from './features/TechStack';
import { ProjectsSection, PanindaModal, AlgoSenseiModal } from './features/Projects';
import { ContactSection } from './features/Contact/ContactSection';
import { Footer } from './features/Footer';
import { Lightbox } from './features/Lightbox';

// ─────────────────────────────────────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────────────────────────────────────

export default function Portfolio() {
  const [scrolled,      setScrolled]      = useState(false);
  const [menuOpen,      setMenuOpen]      = useState(false);
  const [panindaOpen,   setPanindaOpen]   = useState(false);
  const [algoOpen,      setAlgoOpen]      = useState(false);
  const [lightbox,      setLightbox]      = useState(null);
  const [activeSection, setActiveSection] = useState('hero');
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 10);
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const sectionIds = NAV_ITEMS.map(([id]) => id);
    const observers = [];
    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveSection(id); },
        { rootMargin: '-40% 0px -55% 0px' }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  useEffect(() => {
    document.body.style.overflow = (panindaOpen || algoOpen || lightbox || menuOpen) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [panindaOpen, algoOpen, lightbox, menuOpen]);

  useEffect(() => {
    const onEscape = (event) => {
      if (event.key !== 'Escape') return;
      setPanindaOpen(false);
      setAlgoOpen(false);
      setLightbox(null);
      setMenuOpen(false);
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }, 150);
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <>
      <div className="pf-layout">
        <Navigation
          scrolled={scrolled}
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
          onNavigate={scrollTo}
          activeSection={activeSection}
        />

        <main style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <HeroSection />
          <ProjectsSection
            onOpenPaninda={() => setPanindaOpen(true)}
            onOpenAlgoSensei={() => setAlgoOpen(true)}
          />
          <AboutSection />
          <TechStackSection />
          <ContactSection />
        </main>

        <Footer />
      </div>

      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            className="pf-scroll-top"
            onClick={scrollToTop}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.2 }}
            aria-label="Back to top"
          >
            ↑
          </motion.button>
        )}
      </AnimatePresence>

      <Lightbox image={lightbox} onClose={() => setLightbox(null)} />
      <PanindaModal isOpen={panindaOpen} onClose={() => setPanindaOpen(false)} />
      <AlgoSenseiModal isOpen={algoOpen} onClose={() => setAlgoOpen(false)} onOpenLightbox={setLightbox} />
    </>
  );
}
